/**
 * An accessible "what does this mean?" affordance: a small `i` button that reveals a short
 * explanation.
 *
 * Deliberately not hover-only. A hover-only tooltip is invisible to keyboard and touch users, so
 * this opens on **hover, focus, and click**, closes on blur, mouse-leave, and `Escape`, and the
 * revealed text is wired to the trigger with `aria-describedby` so a screen reader announces it
 * rather than announcing a bare letter. The trigger carries an `aria-label` because "i" is not a
 * label, and `type="button"` because the default `submit` would post any form this sits inside.
 *
 * The explanation is rendered only while open and the `aria-describedby` reference is applied only
 * then, so the description is never announced as a dangling reference to a hidden node.
 */

import { useId, useState, type ReactNode } from 'react';

export interface InfoTooltipProps {
  /**
   * Accessible name for the trigger — what the explanation is *about*, e.g. "About generating
   * checks from documentation". Not the explanation itself.
   */
  readonly label: string;
  /** The explanation. Kept to a sentence or two; this is a hint, not documentation. */
  readonly children: ReactNode;
}

export function InfoTooltip({ label, children }: InfoTooltipProps): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);

  return (
    // The mouse handlers sit on the wrapper rather than the trigger so moving the pointer off the
    // button and onto the bubble does not dismiss the text being read.
    <span
      className="info-tooltip"
      data-testid="info-tooltip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-tooltip-trigger"
        aria-label={label}
        aria-expanded={open}
        {...(open ? { 'aria-describedby': id } : {})}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
          }
        }}
      >
        <span aria-hidden="true">i</span>
      </button>
      {open ? (
        <span role="tooltip" id={id} className="info-tooltip-bubble">
          {children}
        </span>
      ) : null}
    </span>
  );
}
